/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: AI Assistant
 🏢 Company: Ring2All LLC
 📄 File: ModuleFormLayout.tsx
 🗓️ Created: 2025-01-18
 🧩 Description:
    Reusable form layout component for module forms.
    Provides consistent structure with form fields and action bar.

 🔧 Usage:
    <ModuleFormLayout
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSaving={isSaving}
      saveText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <YourFormFields />
    </ModuleFormLayout>

 🔁 Last Updated: 2025-01-18 by AI Assistant
────────────────────────────────────────────────────────────────*/

import { ReactNode, FormEvent } from 'react';
import FixedActionBar from '../ui/FixedActionBar';
import { cn } from '../../lib/utils';

type ButtonVariant = 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';

interface ActionButton {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
}

interface ModuleFormLayoutProps {
  children: ReactNode;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving: boolean;
  saveText: string;
  cancelText: string;
  deleteText?: string;
  showSave?: boolean;
  showCancel?: boolean;
  isEditMode?: boolean;
  wrapChildrenInCard?: boolean;
  className?: string;
  spacingClass?: string;
  saveDisabled?: boolean;
  deleteDisabled?: boolean;
  resetDisabled?: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
  showDelete?: boolean;
  onReset?: () => void;
  resetText?: string;
  additionalButtons?: ActionButton[];
  actionBarTitle?: ReactNode;
  actionBarContent?: ReactNode;
}

export function ModuleFormLayout({
  children,
  onSubmit,
  onCancel,
  onDelete,
  isSaving,
  saveText,
  cancelText,
  deleteText,
  showSave = true,
  showCancel = true,
  isEditMode = false,
  wrapChildrenInCard = true,
  className,
  spacingClass = 'space-y-4',
  saveDisabled,
  deleteDisabled,
  resetDisabled,
  isLoading,
  hasChanges,
  showDelete,
  onReset,
  resetText,
  additionalButtons,
  actionBarTitle,
  actionBarContent,
}: ModuleFormLayoutProps) {
  const computedSaveDisabled =
    typeof saveDisabled === 'boolean' ? saveDisabled : hasChanges === false;

  return (
    <div className={cn(spacingClass, 'pb-10', className)}>
      <form onSubmit={onSubmit}>
        {wrapChildrenInCard ? (
          <div className="bg-card p-6 rounded-lg border border-border space-y-6">
            {children}
          </div>
        ) : (
          <div className="space-y-6">{children}</div>
        )}

        {/* Fixed Action Bar */}
        <FixedActionBar
          onSave={onSubmit}
          onCancel={onCancel}
          onDelete={onDelete}
          onReset={onReset}
          isEditMode={isEditMode}
          saveText={saveText}
          cancelText={cancelText}
          deleteText={deleteText}
          resetText={resetText}
          isSaving={isSaving}
          isLoading={isLoading}
          showSave={showSave}
          showCancel={showCancel}
          showDelete={showDelete}
          saveDisabled={computedSaveDisabled}
          deleteDisabled={deleteDisabled}
          resetDisabled={resetDisabled}
          hasChanges={hasChanges}
          additionalButtons={additionalButtons}
          title={actionBarTitle}
        >
          {actionBarContent}
        </FixedActionBar>
      </form>
    </div>
  );
}

export default ModuleFormLayout;

